
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import {
  core_apimessages_CheckoutGooglePayDetails as GooglePayDetails,
} from '@reverbdotcom/commons/src/gql/graphql';
import { useCountries } from '@reverbdotcom/commons/src/useCountries';
import { API_VERSION, GATEWAY } from './googlePayHelpers';

interface Props {
  googlePayDetails: GooglePayDetails;
  callbackIntents: google.payments.api.CallbackIntent[];
  transactionInfo: Omit<google.payments.api.TransactionInfo, 'countryCode'>;
  phoneNumberRequired?: boolean;
  emailRequired?: boolean;
  shippingAddressRequired?: boolean;
}

/**
 * Defines the initial request required for the GooglePayButton component.
 */
export function useGooglePayPaymentRequest({
  googlePayDetails,
  callbackIntents,
  transactionInfo,
  phoneNumberRequired = false,
  shippingAddressRequired = false,
  emailRequired = false,
}: Props): google.payments.api.PaymentDataRequest {
  const allowedCountryCodes = useAllowedCountryCodes();
  const user = useUser();
  const countryCode = user.countryCode || 'US';

  return {
    apiVersion: API_VERSION.apiVersion,
    apiVersionMinor: API_VERSION.apiVersionMinor,
    emailRequired,
    shippingAddressRequired,
    shippingAddressParameters: {
      allowedCountryCodes,
      phoneNumberRequired,
    },
    callbackIntents,
    allowedPaymentMethods: [
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          allowedCardNetworks: googlePayDetails.cardNetworks,
        },
        tokenizationSpecification: {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: GATEWAY,
            gatewayMerchantId: googlePayDetails.gatewayMerchantId,
          },
        },
      },
    ],
    merchantInfo: {
      merchantId: googlePayDetails.merchantId,
      merchantName: googlePayDetails.merchantName,
    },
    transactionInfo: {
      countryCode,
      ...transactionInfo,
    },
  };
}

function useAllowedCountryCodes() {
  const { countries } = useCountries();

  const allowedCountryCodes = countries.map((country) => country.countryCode);

  return allowedCountryCodes;
}
